import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  TablePagination,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EventIcon from '@mui/icons-material/Event';
import GroupIcon from '@mui/icons-material/Group';
import { db } from '../../../../firebase';
import { collection, query, getDocs } from 'firebase/firestore';

// Event interface with metadata
interface Event {
  eventId?: string;          // Internal event ID (for system events)
  importedEventId?: string;  // ID for imported events
  eventName: string;
  eventDate: string;
  eventDescription?: string;
  source: 'internal' | 'imported';
  attendeeCount: number;
  importDate?: string;       // Only for imported events
  lastUpdated: Date;
}

interface EventsTabProps {
  organizationId: string;
}

const EventsTab: React.FC<EventsTabProps> = ({ organizationId }) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [stats, setStats] = useState({
    totalEvents: 0,
    importedEvents: 0,
    internalEvents: 0,
    totalAttendees: 0
  });

  // Fetch all events on component mount
  useEffect(() => {
    if (organizationId) {
      fetchEvents();
    }
  }, [organizationId]);

  // Format date in a friendly way
  const formatDate = (dateString?: string | Date) => {
    if (!dateString) return '—';
    
    try {
      const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
      
      // Check if date is valid
      if (isNaN(date.getTime())) return '—';
      
      // Get today's date at midnight for proper day comparison
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      // Convert date to same format (midnight) for comparison
      const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      
      // For recent dates, show relative time
      const diffTime = Math.abs(today.getTime() - compareDate.getTime());
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      // Convert to Eastern Time
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/New_York'
      };
      
      const fullDateTime = date.toLocaleString('en-US', options) + ' EST';
      
      if (diffDays === 0) {
        // Today
        return `Today at ${date.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true,
          timeZone: 'America/New_York'
        })} EST`;
      } else if (diffDays === 1) {
        // Yesterday
        return `Yesterday at ${date.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true,
          timeZone: 'America/New_York'
        })} EST`;
      }
      
      // Full date and time for older dates
      return fullDateTime;
    } catch (e) {
      return '—';
    }
  };

  // Process contacts to extract unique events with metadata
  const processContactsForEvents = (contacts: any[]): Event[] => {
    const eventMap = new Map<string, Event>();
    
    contacts.forEach(contact => {
      // Process eventAttendance array if it exists
      if (Array.isArray(contact.eventAttendance) && contact.eventAttendance.length > 0) {
        contact.eventAttendance.forEach((event: any) => {
          // Generate a unique key based on the event
          const eventKey = event.eventId || event.importedEventId || `${event.eventName}-${event.eventDate}`;
          
          if (eventMap.has(eventKey)) {
            // Increment attendee count for existing event
            const existingEvent = eventMap.get(eventKey)!;
            eventMap.set(eventKey, {
              ...existingEvent,
              attendeeCount: existingEvent.attendeeCount + 1
            });
          } else {
            // Create new event entry
            eventMap.set(eventKey, {
              eventId: event.eventId,
              importedEventId: event.importedEventId,
              eventName: event.eventName,
              eventDate: event.eventDate,
              eventDescription: event.eventDescription || '',
              source: event.eventId ? 'internal' : 'imported',
              importDate: event.importDate,
              attendeeCount: 1,
              lastUpdated: contact.createdAt || new Date()
            });
          }
        });
      } 
      // Process legacy event data if no eventAttendance array
      else if (contact.eventName) {
        const eventKey = `${contact.eventName}-${contact.eventDate}`;
        
        if (eventMap.has(eventKey)) {
          // Increment attendee count for existing event
          const existingEvent = eventMap.get(eventKey)!;
          eventMap.set(eventKey, {
            ...existingEvent,
            attendeeCount: existingEvent.attendeeCount + 1
          });
        } else {
          // Create new event entry
          eventMap.set(eventKey, {
            eventName: contact.eventName,
            eventDate: contact.eventDate || '',
            eventDescription: contact.eventDescription || '',
            source: 'imported',
            importDate: contact.importDate,
            attendeeCount: 1,
            lastUpdated: contact.createdAt || new Date()
          });
        }
      }
    });
    
    return Array.from(eventMap.values());
  };

  // Fetch all contacts and extract events
  const fetchEvents = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch all contacts from the organization
      const contactsRef = collection(db, `organizations/${organizationId}/contacts`);
      const contactsSnapshot = await getDocs(contactsRef);
      

      
      // Extract contacts data
      const contactsData = contactsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt ? new Date(data.createdAt.seconds * 1000) : new Date()
        };
      });
      
      // Process contacts to extract unique events
      const eventsData = processContactsForEvents(contactsData);
      
      // Update state
      setEvents(eventsData);
      
      // Calculate statistics
      setStats({
        totalEvents: eventsData.length,
        importedEvents: eventsData.filter(event => event.source === 'imported').length,
        internalEvents: eventsData.filter(event => event.source === 'internal').length,
        totalAttendees: eventsData.reduce((sum, event) => sum + event.attendeeCount, 0)
      });
      
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Error loading events data');
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter events based on search query
  const filteredEvents = events.filter(event => {
    const query = searchQuery.toLowerCase();
    return (
      event.eventName.toLowerCase().includes(query) ||
      (event.eventDescription && event.eventDescription.toLowerCase().includes(query))
    );
  });

  // Sort events by date (newest first)
  const sortedEvents = [...filteredEvents].sort((a, b) => {
    const dateA = a.eventDate ? new Date(a.eventDate).getTime() : 0;
    const dateB = b.eventDate ? new Date(b.eventDate).getTime() : 0;
    return dateB - dateA;
  });

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Event Analytics
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Overview of all events across your organization
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Total Events
              </Typography>
              <Typography variant="h4">
                {stats.totalEvents}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Imported Events
              </Typography>
              <Typography variant="h4">
                {stats.importedEvents}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Internal Events
              </Typography>
              <Typography variant="h4">
                {stats.internalEvents}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Total Attendees
              </Typography>
              <Typography variant="h4">
                {stats.totalAttendees}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search events..."
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
      </Box>

      {/* Events Table */}
      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Event Name</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell align="center">Attendees</TableCell>
                    <TableCell>Import Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedEvents
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((event, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <EventIcon 
                              sx={{ 
                                mr: 1, 
                                color: event.source === 'internal' ? 'primary.main' : 'text.secondary' 
                              }} 
                            />
                            <Typography variant="body2">{event.eventName}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{formatDate(event.eventDate)}</TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                            {event.eventDescription || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={event.source === 'internal' ? 'Internal Event' : 'Imported Event'} 
                            size="small"
                            color={event.source === 'internal' ? 'primary' : 'default'}
                            sx={{
                              backgroundColor: event.source === 'internal' ? undefined : '#f0f0f0',
                              fontWeight: 500
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <GroupIcon sx={{ mr: 0.5, fontSize: '1rem', color: 'text.secondary' }} />
                            <Typography variant="body2">{event.attendeeCount}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {event.source === 'imported' && event.importDate ? 
                            formatDate(event.importDate) : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  {filteredEvents.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" sx={{ py: 2 }}>
                          No events found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredEvents.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default EventsTab;
